import React from 'react'
import makeStyles from '@mui/styles/makeStyles'
import {
  Aspect,
  Decision,
  Document,
  RequirementFormConfig,
  Roles,
} from '@shared/types'
import { useFormContext } from 'react-hook-form'
import { FormStatusConnected } from './form-status-connected.component'
import { aspectsEndpoint } from '@modules/aspects/aspects.page.component'
import { decisionsEndpoint } from '@modules/decisions/decisions.page.component'
import { documentsEndpoint } from '@modules/documents/documents.page.component'
import handleDownload from '@shared/utils/handle-download'
import { FormStatusSelection } from './form-select-status.component'
import { RequirementTextField } from './requirement-text-field'
import { FormAuditorAssessmentTemplates } from './templates/form-auditor-assessment-templates.component'
import { FormSummaryTemplate } from './templates/form-summary-template.component'
import { PhotosGrid } from './photos/photos-grid.component'
import { FormConnectedRequirements } from './form-connected-requirements.component'
import IDS from '@shared/onboarding/ids'
import { useACL } from '@shared/hooks/use-acl.hook'
import { useDisableDuringOngoingAudit } from '@modules/company-profile/use-disable-during-ongoing-audit.hook'
import DisabledTooltip from '@shared/components/disabled-tooltip.component'
import { TextField } from '@mui/material'

interface Props {
  formConfig: RequirementFormConfig
  companyID: string
}

export const RequirementForm = ({ formConfig, companyID }: Props) => {
  const { register, control } = useFormContext()
  const { is } = useACL()
  const { fields, allowTemplates, requirementBaseID } = formConfig
  const isClient = is([Roles.ClientBasic, Roles.Client])

  const { shouldDisable: shouldDisableForSelectedClientRoles, tooltipText } =
    useDisableDuringOngoingAudit(companyID)

  const shouldShowField = (field?: {
    show: boolean
    editable: boolean
    defaultValue?: any
  }): boolean => {
    if (!field?.show) return false
    if (field.editable) return true

    const value = field.defaultValue

    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'string') return value !== '-'
    return Boolean(value)
  }

  return (
    <>
      <div>
        {shouldShowField(fields?.connectedAspects) && (
          <div
            id={
              isClient
                ? IDS.CLIENT_CLIENT_BASIC_REQUIREMENT_ASPECTS
                : IDS.REQUIREMENT_ASPECTS
            }
          >
            <DisabledTooltip disabled={shouldDisableForSelectedClientRoles} title={tooltipText}>
              <FormStatusConnected<Aspect>
                formControl={control}
                label="Aspekty"
                endpoint={`companies/${companyID}/${aspectsEndpoint}`}
                fieldName="connectedAspects"
                isPreviewMode={!fields?.connectedAspects?.editable}
                disabled={shouldDisableForSelectedClientRoles}
              />
            </DisabledTooltip>
          </div>
        )}
        {shouldShowField(fields?.connectedDecisions) && (
          <div
            id={
              isClient
                ? IDS.CLIENT_CLIENT_BASIC_REQUIREMENT_DECISIONS
                : IDS.REQUIREMENT_DECISIONS
            }
          >
            <DisabledTooltip disabled={shouldDisableForSelectedClientRoles} title={tooltipText}>
              <FormStatusConnected<Decision>
                formControl={control}
                fieldName="connectedDecisions"
                label="Decyzje"
                endpoint={`companies/${companyID}/${decisionsEndpoint}`}
                isPreviewMode={!fields?.connectedDecisions?.editable}
                disabled={shouldDisableForSelectedClientRoles}
                onPressOption={(object) =>
                  handleDownload(
                    `companies/${companyID}/${decisionsEndpoint}/${object.id}/download`,
                    object?.fileName
                  )
                }
              />
            </DisabledTooltip>
          </div>
        )}
        {shouldShowField(fields?.connectedDocuments) && (
          <div
            id={
              isClient
                ? IDS.CLIENT_CLIENT_BASIC_REQUIREMENT_DOCUMENT
                : IDS.REQUIREMENT_DOCUMENT
            }
          >
            <DisabledTooltip disabled={shouldDisableForSelectedClientRoles} title={tooltipText}>
              <FormStatusConnected<Document>
                formControl={control}
                fieldName="connectedDocuments"
                label="Dokumenty"
                endpoint={`companies/${companyID}/${documentsEndpoint}`}
                isPreviewMode={!fields?.connectedDocuments?.editable}
                disabled={shouldDisableForSelectedClientRoles}
                onPressOption={(object) =>
                  handleDownload(
                    `companies/${companyID}/${documentsEndpoint}/${object.id}/download`,
                    object?.fileName
                  )
                }
              />
            </DisabledTooltip>
          </div>
        )}
      </div>
      {shouldShowField(fields?.auditorStatusID) && (
        <FormStatusSelection
          title="Wskaż status"
          editable={fields?.auditorStatusID?.editable}
        />
      )}
      {shouldShowField(fields?.auditorAssessment) && (
        <>
          <RequirementTextField
            name="auditorAssessment"
            label="Realizacja wymagania"
            editable={fields?.auditorAssessment?.editable}
            rows={12}
          />
          {allowTemplates && requirementBaseID && (
            <FormAuditorAssessmentTemplates
              requirementBaseID={requirementBaseID}
            />
          )}
        </>
      )}
      {shouldShowField(fields.photos) && (
        <DisabledTooltip disabled={shouldDisableForSelectedClientRoles} title={tooltipText}>
          <PhotosGrid
            editable={fields?.photos?.editable && !shouldDisableForSelectedClientRoles}
            formConfig={formConfig}
          />
        </DisabledTooltip>
      )}
      {shouldShowField(fields.connectedAuditRequirements) && (
        <FormConnectedRequirements
          label="Powiązane wymagania"
          editable={fields?.connectedAuditRequirements?.editable}
          companyID={Number(companyID)}
          formConfig={formConfig}
        />
      )}
      {shouldShowField(fields.fixDate) && (
        <RequirementTextField
          name="fixDate"
          label="Termin realizacji"
          editable={fields.fixDate?.editable}
        />
      )}

      {shouldShowField(fields.clientStatusID) && (
        <div id={IDS.REQUIREMENT_ASSESSMENT}>
          <DisabledTooltip disabled={shouldDisableForSelectedClientRoles} title={tooltipText}>
            <FormStatusSelection
              title="Ocena specjalisty"
              isClient
              editable={fields?.clientStatusID?.editable && !shouldDisableForSelectedClientRoles}
            />
          </DisabledTooltip>
        </div>
      )}
      {shouldShowField(fields.clientComment) && (
        <>
          <DisabledTooltip disabled={shouldDisableForSelectedClientRoles} title={tooltipText}>
            <TextField
              {...register('clientComment')}
              defaultValue={fields?.clientComment?.defaultValue}
              name="clientComment"
              fullWidth
              multiline
              maxRows={20}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              label="Komentarz specjalisty"
              disabled={!fields?.clientComment?.editable || shouldDisableForSelectedClientRoles}
              InputProps={{
                sx: {
                  '&.Mui-disabled': {
                    color: '#4a4a4a',
                    WebkitTextFillColor: '#4a4a4a',
                    opacity: 1,
                  },
                  '&.Mui-disabled textarea': {
                    color: '#4a4a4a',
                    WebkitTextFillColor: '#4a4a4a',
                    opacity: 1,
                  },
                },
              }}
            />
          </DisabledTooltip>
        </>
      )}

      {shouldShowField(fields.summary) && (
        <>
          <RequirementTextField
            name="summary"
            label="Podsumowanie"
            editable={fields?.summary?.editable}
            rows={6}
          />
          {allowTemplates && requirementBaseID && (
            <FormSummaryTemplate requirementBaseID={requirementBaseID} />
          )}
        </>
      )}
    </>
  )
}

export const useStyles = makeStyles((theme) => {
  return {}
})
